import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { tabContentVariants, springPresets } from "@/lib/motion"
import { cn } from "@/lib/utils"

interface Tab {
  id: string
  label: string
  icon?: React.ReactNode
  content: React.ReactNode
}

interface AnimatedTabsProps {
  tabs: Tab[]
  variant?: "underline" | "pill" | "gradient-pill"
  defaultTab?: string
  className?: string
}

export function AnimatedTabs({
  tabs,
  variant = "underline",
  defaultTab,
  className,
}: AnimatedTabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || "")

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content

  return (
    <div className={cn("w-full", className)}>
      <div
        className={cn(
          "flex items-center gap-1 mb-8 overflow-x-auto scrollbar-none",
          variant === "underline" && "border-b border-border",
          (variant === "pill" || variant === "gradient-pill") &&
            "bg-card/50 p-1 rounded-xl"
        )}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "relative px-5 py-3 text-sm font-semibold transition-all duration-200 whitespace-nowrap",
              "hover:scale-[1.03] active:scale-95",
              variant === "underline" && "pb-4",
              (variant === "pill" || variant === "gradient-pill") &&
                "rounded-lg",
              activeTab === tab.id
                ? "text-primary"
                : "text-muted-foreground hover:text-accent"
            )}
          >
            {variant === "underline" && activeTab === tab.id && (
              <motion.div
                layoutId="underline"
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary"
                style={{ boxShadow: "0 0 8px 1px var(--color-primary, #C9A84C)" }}
                transition={springPresets.snappy}
              />
            )}

            {variant === "pill" && activeTab === tab.id && (
              <motion.div
                layoutId="pill"
                className="absolute inset-0 bg-primary/15 rounded-lg border border-primary/40"
                transition={springPresets.snappy}
              />
            )}

            {variant === "gradient-pill" && activeTab === tab.id && (
              <motion.div
                layoutId="gradient-pill"
                className="absolute inset-0 rounded-lg border border-primary/40"
                style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.20) 0%, rgba(201,168,76,0.08) 50%, rgba(201,168,76,0.20) 100%)" }}
                transition={springPresets.snappy}
              />
            )}

            <span className="relative z-10 flex items-center gap-2">
              {tab.icon && (
                <motion.span
                  whileHover={{ scale: 1.1 }}
                  transition={springPresets.snappy}
                >
                  {tab.icon}
                </motion.span>
              )}
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          variants={tabContentVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="min-h-[200px]"
        >
          {activeTabContent}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
